import { Injectable } from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from '@cupoui/core/services/http.service';
import { IDebtor, IDebtorOverrule } from '@cupoui/products-contracts/models';
import { SalesOrder, SalesOrderResponse } from '@cupoui/shared/model/sales-order';
import { SalesOrderStatusEnum } from '@cupoui/shared/model/enumerations/sales-order-status.enum';
import { DATE_FORMAT } from '@cupoui/shared/constants/input.constants';
import moment from 'moment';
import { createRequestOption, Pagination } from '@cupoui/shared/util/request-util';
import { NGXLogger } from 'ngx-logger';
import { BusinessAreaEnum } from '@cupoui/shared/model/enumerations/business-area.enum';

@Injectable({
  providedIn: 'root'
})
export class SalesOrderService {
  constructor(private logger: NGXLogger, private httpService: HttpService) {}
  rolesUpdated = new Subject<string>();

  updateRoles(text: string) {
    this.rolesUpdated.next(text);
  }

  getAllDebtors(): Observable<IDebtor[]> {
    return this.httpService.get<IDebtor[]>(`/services/onepoint/api/common-customers/debtors`).pipe(
      map(debtors => {
        const selectedCustomerProfileIds = localStorage.getItem('selected-customer-profile-ids')?.split(',');
        return debtors.filter(
          debtor =>
            !selectedCustomerProfileIds?.length ||
            debtor.contractProfileIds.some(contractProfileId => selectedCustomerProfileIds.includes(contractProfileId.toString()))
        );
      })
    );
  }

  getDebtorNames(filter: string | null, customerIds: Array<number> | null): Observable<IDebtor[]> {
    const queryParams: { [key: string]: string } = {};
    if (filter !== null) {
      queryParams.filter = filter;
    }
    if (customerIds !== null && customerIds?.length > 0) {
      queryParams.customerProfilesIds = customerIds.join(',');
    }
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');

    // Make the HTTP GET request with the constructed query string
    return this.httpService
      .get<IDebtor[]>(`/services/onepoint/api/common-customers/debtor-names${queryString ? '?' + queryString : ''}`)
      .pipe(
        map(debtors => {
          const selectedCustomerProfileIds = localStorage.getItem('selected-customer-profile-ids')?.split(',');
          return debtors.filter(
            debtor =>
              !selectedCustomerProfileIds?.length ||
              debtor.contractProfileIds.some(contractProfileId => selectedCustomerProfileIds.includes(contractProfileId.toString()))
          );
        })
      );
  }

  getDebtorsWithOverrides(profileIds: number[], businessArea: BusinessAreaEnum): Observable<Map<number, IDebtor[]>> {
    let params = `businessArea=${businessArea.toUpperCase()}`;
    if (profileIds && profileIds.length > 0) {
      const profileIdsAsText = profileIds.join(',');
      params += `&customerProfilesIds=${profileIdsAsText}`;
    }
    return this.httpService
      .get<Record<string, IDebtor[]>>(`/services/onepoint/api/common-customers/debtors-by-business-area-and-profile?${params}`)
      .pipe(
        map(obj => new Map<number, IDebtor[]>(Object.entries(obj ?? {})
          .map(([key, value]) => [Number(key), value])))
      );
  }

  getProfileDebtorOverruleList(profileId: number): Observable<IDebtorOverrule[]> {
    return this.httpService.get<IDebtorOverrule[]>(`/services/onepoint/api/debtor-overrule/profile/${profileId}`);
  }

  // TODO FIX getAllProfileDebtorOverruleList() method
  getAllProfileDebtorOverruleList(profileIds: number[]): Observable<IDebtorOverrule[][]> {
    return of<IDebtorOverrule[][]>([]);
  }

  public getCustomerProfiles(ids: number[]): Observable<any[]> {
    return this.httpService.post('/services/onepoint/api/profile/ids', ids);
  }

  public getSalesOrders(caseId: string): Observable<SalesOrder[]> {
    return this.httpService.get<SalesOrder[]>(`/services/caseeconomy/api/sales-orders/case/${caseId}`).pipe(
      map(salesOrders =>
        salesOrders.map(salesOrder => {
          salesOrder.customerInvoiceExclVatCurrency =
            salesOrder.status === SalesOrderStatusEnum.INVOICED
              ? salesOrder.customerInvoiceExclVat?.currency
              : salesOrder.invoicePrice?.currency;
          salesOrder.customerInvoiceExclVatAmount =
            salesOrder.status === SalesOrderStatusEnum.INVOICED
              ? salesOrder.customerInvoiceExclVat?.amount
              : salesOrder.invoicePrice?.amount;
          salesOrder.customerInvoiceInclVatCurrency = salesOrder.customerInvoiceInclVat?.currency;
          salesOrder.customerInvoiceInclVatAmount = salesOrder.customerInvoiceInclVat?.amount;
          return salesOrder;
        })
      )
    );
  }

  public getSalesOrderById(id: string): Observable<SalesOrder> {
    return this.httpService.get(`/services/caseeconomy/api/sales-orders/${id}`);
  }

  public getSalesOrdersByInvoiceNo(invoiceNo: string, businessArea: BusinessAreaEnum): Observable<SalesOrder[]> {
    let url: string;
    if (businessArea === BusinessAreaEnum.Travel) {
      url = '/services/caseeconomy/api/sales-orders/getByInvoiceNo';
    } else if (businessArea === BusinessAreaEnum.Mobility) {
      url = '/services/axapta/api/invoices';
    }
    return this.httpService.get<SalesOrder[]>(`${url}?invoiceNo=${invoiceNo}`).pipe(
      map(salesOrders =>
        salesOrders.map(salesOrder => {
          salesOrder.customerInvoiceExclVatCurrency =
            salesOrder.status === SalesOrderStatusEnum.INVOICED
              ? salesOrder.customerInvoiceExclVat?.currency
              : salesOrder.invoicePrice?.currency;
          salesOrder.customerInvoiceExclVatAmount =
            salesOrder.status === SalesOrderStatusEnum.INVOICED
              ? salesOrder.customerInvoiceExclVat?.amount
              : salesOrder.invoicePrice?.amount;
          salesOrder.customerInvoiceInclVatCurrency = salesOrder.customerInvoiceInclVat?.currency;
          salesOrder.customerInvoiceInclVatAmount = salesOrder.customerInvoiceInclVat?.amount;
          salesOrder.invoiceDate = moment.utc(salesOrder.invoiceDate);
          return salesOrder;
        })
      )
    );
  }

  public getSalesOrdersByDebtorAndInvoiceDatePeriod(
    businessArea: BusinessAreaEnum,
    debtor: string,
    customerProfile: string | undefined,
    from: moment.Moment,
    to: moment.Moment,
    caseTypeCodes: string | undefined,
    costType: string | undefined,
    status: string | undefined,
    filterText: string | undefined,
    invoiceHandlingNoteEmpty: boolean | undefined,
    pagination: Pagination
  ): Observable<SalesOrderResponse> {
    let options = createRequestOption(pagination).append('sort', 'id');

    const queryParams = {
      debtorNumber: debtor,
      customerProfileId: customerProfile,
      fromInvoiceDate: from.format(DATE_FORMAT),
      toInvoiceDate: to.format(DATE_FORMAT),
      caseTypeCodes: caseTypeCodes,
      costType: costType,
      status: status,
      filterText: filterText,
      invoiceHandlingNoteEmpty: invoiceHandlingNoteEmpty
    };

    options = Object.entries(queryParams)
      .filter(([k, v]: [string, any]) => !!v)
      .reduce((options, [k, v]: [string, any]) => options.set(k, v), options);

    let url: string;
    if (businessArea === BusinessAreaEnum.Travel) {
      url = '/services/caseeconomy/api/sales-orders/getByDebtorAndPeriod';
    } else if (businessArea === BusinessAreaEnum.Mobility) {
      url = '/services/axapta/api/invoices/search';
    }

    return this.httpService
      .get<any>(url, { params: options, observe: 'response' })
      .pipe(
        map(response => {
          var sizeRegExp: RegExp = /.*size=(\d+)>;.rel="next"/gm;
          var pageRegExp: RegExp = /.*page=(\d+).*rel="next"/gm;
          var link: string = response.headers.get('link');
          var page = pageRegExp.exec(link);
          var size = sizeRegExp?.exec(link);
          return {
            pagination: {
              page: Number(page ? page[0] : 1) - 1,
              size: Number(size ? size[0] : 0),
              total: response.headers.get('x-total-count')
            },
            salesOrders: (response['body'] as SalesOrder[]).map(salesOrder => {
              salesOrder.customerInvoiceExclVatCurrency =
                salesOrder.status === SalesOrderStatusEnum.INVOICED
                  ? salesOrder.customerInvoiceExclVat?.currency
                  : salesOrder.invoicePrice?.currency;
              salesOrder.customerInvoiceExclVatAmount =
                salesOrder.status === SalesOrderStatusEnum.INVOICED
                  ? salesOrder.customerInvoiceExclVat?.amount
                  : salesOrder.invoicePrice?.amount;
              salesOrder.customerInvoiceInclVatCurrency = salesOrder.customerInvoiceInclVat?.currency;
              salesOrder.customerInvoiceInclVatAmount = salesOrder.customerInvoiceInclVat?.amount;
              salesOrder.invoiceDate = moment.utc(salesOrder.invoiceDate);

              return salesOrder;
            })
          };
        })
      );
  }

  public updateInvoiceHandlingNote(invoice: SalesOrder): Observable<void> {
    return this.httpService.put<void>(`/services/caseeconomy/api/sales-orders/updateInvoiceHandlingNote/${invoice.seccaCaseId}`, invoice);
  }
}
